import axios from "axios";
import env from "../env";

const DELETED_ASSET_IDS_KEY = "assetManagement_deletedAssetIds";

const getAssetsUrl = (assetId) => {
  const baseUrl = `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/appsupport/assets`;
  return assetId ? `${baseUrl}/${encodeURIComponent(assetId)}` : baseUrl;
};

const buildAssetQueryParams = (tag, resolution) => {
  const params = {};
  const trimmedTag = tag?.trim();

  if (trimmedTag) {
    params.tag = trimmedTag;
  }

  if (resolution) {
    params.resolution = resolution;
  }

  return params;
};

const getDeletedAssetIds = () => {
  try {
    const stored = sessionStorage.getItem(DELETED_ASSET_IDS_KEY);
    const parsed = stored ? JSON.parse(stored) : [];

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const markAssetAsDeleted = (assetId) => {
  if (!assetId) {
    return;
  }

  const deletedIds = getDeletedAssetIds();

  if (!deletedIds.includes(assetId)) {
    sessionStorage.setItem(
      DELETED_ASSET_IDS_KEY,
      JSON.stringify([...deletedIds, assetId])
    );
  }
};

const unmarkAssetAsDeleted = (assetId) => {
  if (!assetId) {
    return;
  }

  const deletedIds = getDeletedAssetIds().filter((id) => id !== assetId);
  sessionStorage.setItem(DELETED_ASSET_IDS_KEY, JSON.stringify(deletedIds));
};

const createNotFoundError = () => {
  const error = new Error("Asset not found.");
  error.response = { status: 404, data: { message: "Asset not found." } };
  return error;
};

const performDeleteAsset = (assetId, tag, resolution) => {
  return axios.delete(getAssetsUrl(assetId?.trim()), {
    headers: {
      "Content-Type": "application/json",
    },
    params: buildAssetQueryParams(tag, resolution),
  });
};

const verifyAssetExists = async (assetId, tag, resolution) => {
  try {
    const res = await axios.get(getAssetsUrl(assetId?.trim()), {
      headers: {
        "Content-Type": "application/json",
      },
      params: buildAssetQueryParams(tag, resolution),
    });
    const data = res?.data;

    if (!data) {
      return false;
    }

    if (typeof data === "string" && data.trim()) {
      return true;
    }

    if (typeof data === "object") {
      return Boolean(data.url || data.assetId || data.id);
    }

    return false;
  } catch (error) {
    const errMsg = error?.response?.data?.message;

    if (error?.response?.status === 404) {
      return false;
    }

    if (
      typeof errMsg === "string" &&
      (errMsg.toLowerCase().includes("not found") ||
        errMsg.toLowerCase().includes("does not exist"))
    ) {
      return false;
    }

    throw error;
  }
};

const ensureAssetExists = async (
  assetId,
  tag,
  resolution,
  { requireViewCheck = false, useSessionCheck = false } = {}
) => {
  const trimmedAssetId = assetId?.trim();
  const trimmedTag = tag?.trim();

  if (!trimmedAssetId) {
    throw createNotFoundError();
  }

  if (useSessionCheck && getDeletedAssetIds().includes(trimmedAssetId)) {
    throw createNotFoundError();
  }

  if (requireViewCheck || (trimmedTag && resolution)) {
    const exists = await verifyAssetExists(
      trimmedAssetId,
      trimmedTag,
      resolution
    );

    if (!exists) {
      throw createNotFoundError();
    }
  }
};

export const clearDeletedAssetMark = (assetId) => {
  unmarkAssetAsDeleted(assetId?.trim());
};

export const uploadAsset = (formData) => {
  return axios.post(getAssetsUrl(), formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/** Adds or updates one tag/resolution variant under an existing assetId (POST only). */
export const updateAsset = (formData) => uploadAsset(formData);

export const getAssetErrorMessage = (
  error,
  fallback = "Request failed",
  notFoundMessage = "Asset not found. Please verify the Asset ID, Tag, and Resolution.",
) => {
  const status = error?.response?.status;
  const errMsg = error?.response?.data?.message || error?.message || fallback;

  if (
    status === 404 ||
    (typeof errMsg === "string" &&
      (errMsg.toLowerCase().includes("not found") ||
        errMsg.toLowerCase().includes("does not exist")))
  ) {
    return notFoundMessage;
  }

  if (
    typeof errMsg === "string" &&
    errMsg.toLowerCase().includes("already exist")
  ) {
    return errMsg;
  }

  return errMsg;
};

export const getAssetMetadata = (assetId) => {
  const baseUrl = `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/appsupport/assets/metadata`;

  return axios.get(`${baseUrl}/${encodeURIComponent(assetId)}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const viewAsset = (assetId, tag, resolution) => {
  return axios.get(getAssetsUrl(assetId), {
    headers: {
      "Content-Type": "application/json",
    },
    params: buildAssetQueryParams(tag, resolution),
  });
};

export const deleteAsset = async (assetId, tag, resolution) => {
  const trimmedAssetId = assetId?.trim();
  const trimmedTag = tag?.trim();

  await ensureAssetExists(assetId, tag, resolution, {
    useSessionCheck: true,
  });

  const response = await performDeleteAsset(
    trimmedAssetId,
    trimmedTag,
    resolution
  );
  markAssetAsDeleted(trimmedAssetId);

  return response;
};

export const extractAssetId = (data, fallback = "") => {
  if (!data) {
    return fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  return data.assetId || data.id || fallback;
};
